import React from "react";
import { X } from "react-feather";
import {
  Field,
  Chip,
  ChipLabel,
  ChipRemove,
  Input,
  ErrorText,
} from "./ChipInput.styles";

export interface ChipInputProps {
  /** The currently committed chips. */
  values: string[];
  /** Current draft input text (controlled). */
  draft: string;
  /** True to show the invalid-input styling and errorText. */
  error?: boolean;
  /** Called when the user clicks the "x" on a chip. */
  onRemove: (value: string) => void;
  /** Change handler for the draft input. */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Handles Enter / comma / Backspace commit and delete semantics. */
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Commits any remaining draft on blur. */
  onBlur: () => void;
  placeholder?: string;
  errorText?: string;
  /** Input `type` attribute (default "text"). */
  type?: React.InputHTMLAttributes<HTMLInputElement>["type"];
  ariaLabel?: string;
  disabled?: boolean;
}

const ChipInput: React.FC<ChipInputProps> = ({
  values,
  draft,
  error = false,
  onRemove,
  onChange,
  onKeyDown,
  onBlur,
  placeholder,
  errorText,
  type = "text",
  ariaLabel,
  disabled = false,
}) => (
  <>
    <Field $error={error} $disabled={disabled}>
      {values.map((value) => (
        <Chip key={value}>
          <ChipLabel>{value}</ChipLabel>
          <ChipRemove
            type="button"
            aria-label={`Remove ${value}`}
            onClick={() => onRemove(value)}
            disabled={disabled}
          >
            <X size={12} />
          </ChipRemove>
        </Chip>
      ))}
      <Input
        type={type}
        value={draft}
        placeholder={values.length === 0 ? placeholder : ""}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
      />
    </Field>
    {error && errorText && <ErrorText role="alert">{errorText}</ErrorText>}
  </>
);

export default ChipInput;
